import { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Store
import { useAppSelector } from 'store';
import { selectPageValidation } from 'store/slices/pageValidation';
// Import UI Components
import { Dropdown, ExtendedInput, Tooltip, Icon, Checkbox } from '@geneui/components';
// Import Components
import AccessControl from 'components/common/AccessControl';
import ValidatableDatePickerInput from 'components/common/ValidatableDatePickerInput';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Constants
import { TemplateTypesLabels, l, GoalDropdownOptions, MonthDayYearFormat, PARTNER_SETTINGS } from 'constants/common';
// Import Services
import { UtilsHttpService } from 'services/http';
import Helpers from 'services/Helpers';
// Import Enums
import { PermissionEnum } from 'enums';

const { customMoment } = Helpers;

const CampaignGeneralDetails = ({
    selectedProvider,
    selectedGoalType,
    setSelectedProvider,
    setSelectedGoalType,
    setGoalCount,
    goalCount,
    setAnalysisDate,
    analysisDate,
    selectedChannel,
    isNonMarketing,
    setIsNonMarketing,
    isPublic,
    setIsPublic,
}) => {
    const { t } = useTranslation();

    const pageValidation = useAppSelector(selectPageValidation);
    const { partnerDateFormat } = usePartnerDateFormats();

    const { doPostRequest } = useRequest();
    const [providers, setProviders] = useState([]);
    const [isDisableProviders, setIsDisableProviders] = useState(isNil(selectedChannel) || selectedChannel === l.Empty);
    const [isDisableGoalCount, setIsDisableGoalCount] = useState(isNil(goalCount) || goalCount === 0);

    const { getPartnerMessageProvidersRequest } = useCancelableRequests(
        { getPartnerMessageProvidersRequest: UtilsHttpService.getPartnerMessageProviders },
        { takeLatest: ['getPartnerMessageProvidersRequest'] },
    );

    const providerOnChangeHandler = (e) => {
        isNil(e) ? setSelectedProvider(null) : setSelectedProvider(e.value);
    };

    const goalTypeOnChangeHandler = (e) => {
        setIsDisableGoalCount(isNil(e));
        setSelectedGoalType(e?.value);
    };

    const countOnChangeHandler = (e) => {
        const { validity, value } = e.target;

        if (validity.valid && (value !== '' || value === 0)) {
            +value[0] === 0 && value.length !== 1 ? setGoalCount(`${value.substr(1)}`) : setGoalCount(`${value}`);
        } else if (value === '') {
            validity.valid ? setGoalCount(null) : setGoalCount(`${goalCount}`);
        }
    };

    const handleIsNonMarketingChange = (e) => {
        setIsNonMarketing(e.target.checked);
    };

    const handleIsPublicChange = (e) => {
        setIsPublic(e.target.checked);
    };

    const init = () => {
        if (
            [TemplateTypesLabels[TemplateTypesLabels.Email], TemplateTypesLabels[TemplateTypesLabels.SMS]].includes(
                selectedChannel,
            )
        ) {
            setIsDisableProviders(false);
        } else {
            setIsDisableProviders(true);
        }
        setIsDisableGoalCount(isNil(selectedGoalType));

        doPostRequest(getPartnerMessageProvidersRequest.request, {
            requestBody: { DeliveryMethod: TemplateTypesLabels[selectedChannel] },
            successCallback: (Data) => {
                setProviders(
                    Data.map(({ Id, Name }) => {
                        return {
                            value: +Id,
                            label: Name,
                        };
                    }),
                );
            },
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, [selectedChannel]);

    return (
        <>
            <div className="chanel-row">
                <ExtendedInput
                    title={t(l.Channel)}
                    label={t(l.Channel)}
                    placeholder={t(l.Channel)}
                    labelAppearance="swap"
                    disabled={true}
                    min={1}
                    value={t(selectedChannel)}
                />
                <Dropdown
                    placeholder={t(l.Providers)}
                    labelAppearance="swap"
                    isMultiSelect={false}
                    disabled={isDisableProviders}
                    hasSearch={true}
                    inputSize="default"
                    appearance="outline"
                    data={providers}
                    searchPlaceholderText={t(l.Search)}
                    noDataText={t(l.NoDataFound)}
                    value={selectedProvider}
                    onChange={providerOnChangeHandler}
                    clearable={true}
                />
            </div>
            <div className="collect-row">
                <ValidatableDatePickerInput
                    title={t(l.CollectAnalysisBy)}
                    labelAppearance="swap"
                    onChange={setAnalysisDate}
                    value={analysisDate}
                    isValid={!t(pageValidation?.CollectDate?.errorText)}
                    errorText={t(pageValidation?.CollectDate?.errorText)}
                    isTouched={true}
                    format={partnerDateFormat}
                    min={customMoment().format(MonthDayYearFormat)}
                />
                <Tooltip text={t(l.TooltipCollectAnalysisBy)} position="bottom">
                    <Icon type="bc-icon-info" />
                </Tooltip>
            </div>
            <div className="non-market">
                <AccessControl permissions={PermissionEnum.ManageNonMarketingCampaign}>
                    <Checkbox
                        label={t(l.IsNonMarketing)}
                        checked={isNonMarketing}
                        onChange={handleIsNonMarketingChange}
                        disabled={isPublic}
                    />
                </AccessControl>
            </div>
            <div className="non-market">
                <AccessControl partnerSetting={PARTNER_SETTINGS.IsAllowABTest}>
                    <Checkbox
                        label={t(l.PublicCampaign)}
                        checked={isPublic}
                        onChange={handleIsPublicChange}
                        disabled={isNonMarketing}
                    />
                </AccessControl>
            </div>
            <div className="goal-row">
                <Dropdown
                    placeholder={t(l.GoalType)}
                    labelAppearance="swap"
                    isMultiSelect={false}
                    disabled={false}
                    hasSearch={true}
                    inputSize="default"
                    appearance="outline"
                    data={GoalDropdownOptions}
                    searchPlaceholderText="Search"
                    noDataText="No data found"
                    value={selectedGoalType}
                    onChange={goalTypeOnChangeHandler}
                    clearable={true}
                />
                <ExtendedInput
                    placeholder={t(l.Count)}
                    labelAppearance="swap"
                    pattern="[0-9]*"
                    type="number"
                    min={1}
                    onChange={countOnChangeHandler}
                    value={goalCount}
                    disabled={isDisableGoalCount}
                />
            </div>
        </>
    );
};

CampaignGeneralDetails.propTypes = {
    isNonMarketing: PropTypes.bool,
    isPublic: PropTypes.bool,
    goalCount: PropTypes.number,
    selectedProvider: PropTypes.number,
    analysisDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    selectedGoalType: PropTypes.string,
    selectedChannel: PropTypes.string,
    setSelectedProvider: PropTypes.func,
    setSelectedGoalType: PropTypes.func,
    setGoalCount: PropTypes.func,
    setAnalysisDate: PropTypes.func,
    setIsNonMarketing: PropTypes.func,
    setIsPublic: PropTypes.func,
};

export default memo(CampaignGeneralDetails);
